import * as React from "react"

/**
 * Hace que el botón "Atrás" del navegador/móvil cierre el modal abierto
 * (Sheet, Dialog, AlertDialog) en lugar de salir de la página.
 *
 * Robusto frente a:
 *  - Límite del navegador de pushState (100 por 10s) → throttle + try/catch para
 *    no llegar a lanzar el SecurityError que tumbaba la app.
 *  - Cierre por la X: solo deshace (history.back) la entrada que ESTE modal
 *    empujó realmente, así nunca navega fuera de la página por error.
 */

type CloseFn = () => void

const closeStack: CloseFn[] = []
let listenerAttached = false

// Throttle global: nos mantenemos muy por debajo del límite del navegador.
const recentPushes: number[] = []
function canPush(): boolean {
  if (typeof window === "undefined") return false
  const now = Date.now()
  while (recentPushes.length && now - recentPushes[0] > 10000) recentPushes.shift()
  if (recentPushes.length >= 40) return false
  recentPushes.push(now)
  return true
}

function ensureGlobalListener() {
  if (listenerAttached || typeof window === "undefined") return
  listenerAttached = true
  window.addEventListener("popstate", () => {
    const top = closeStack[closeStack.length - 1]
    if (top) top()
  })
}

export function useModalBackButton() {
  React.useEffect(() => {
    if (typeof window === "undefined") return
    ensureGlobalListener()

    const marker = { __jModal: Date.now() + Math.random() }
    let pushed = false
    if (canPush()) {
      try {
        window.history.pushState(marker, "")
        pushed = true
      } catch {
        pushed = false
      }
    }
    // Si no pudimos empujar (throttle/error), no registramos nada: el modal se
    // cierra con la X / clic fuera con normalidad, solo sin soporte de "Atrás".
    if (!pushed) return

    const close: CloseFn = () => {
      try {
        document.dispatchEvent(
          new KeyboardEvent("keydown", {
            key: "Escape",
            code: "Escape",
            keyCode: 27,
            which: 27,
            bubbles: true,
            cancelable: true,
          })
        )
      } catch { /* noop */ }
    }
    closeStack.push(close)

    return () => {
      const idx = closeStack.lastIndexOf(close)
      if (idx !== -1) closeStack.splice(idx, 1)

      // Solo deshacer si nuestra entrada sigue arriba (cierre por X / clic fuera,
      // no por "Atrás"). Nunca navegar fuera de la página por error.
      try {
        const state = window.history.state as { __jModal?: number } | null
        if (state && state.__jModal === marker.__jModal) {
          window.history.back()
        }
      } catch { /* noop */ }
    }
  }, [])
}
